import { ServiceLayer } from '@packages/modules';
import { Dialog, Image, Stack, Typography } from '@packages/shared';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { useLayerTracking } from '../Tracking/hooks/useLayerTracking';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';

const ProductHintsItemFragment = graphql(/* GraphQL */ `
  fragment ProductHintsItemFragment on Hint {
    iconUrl
    text
    serviceLayerId
  }
`);

export type ProductHintsItemProps = {
  data: FragmentType<typeof ProductHintsItemFragment>;
};

const messages = defineMessages({
  productHintsCardHeader: {
    id: 'productHintsCard.header',
    defaultMessage: 'Hinweise',
  },
});

/**
 * ProductHintsCard component displays all product hints
 * */
export const ProductHintsItem = ({ data }: ProductHintsItemProps) => {
  const { iconUrl, serviceLayerId, text } = getFragmentData(ProductHintsItemFragment, data);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { trackViewLayer } = useLayerTracking();
  const { trackServiceLayerClick } = useServiceLayerTracking();

  const handleClick = () => {
    trackViewLayer(text);
    // handler can only be dispatched if a serviceLayerId is present
    trackServiceLayerClick(serviceLayerId!);

    setIsDialogOpen(true);
  };
  const { formatMessage } = useIntl();

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        {...(serviceLayerId && {
          onClick: handleClick,
          role: 'button',
          sx: {
            cursor: 'pointer',
          },
        })}
      >
        <Image src={iconUrl} alt={text} height={24} width={24} />
        <Typography variant="body2">{text}</Typography>
      </Stack>
      {serviceLayerId !== null && (
        <Dialog
          title={formatMessage(messages.productHintsCardHeader)}
          open={isDialogOpen}
          dialogContent={<ServiceLayer serviceLayerId={serviceLayerId} />}
          handleClose={() => setIsDialogOpen(false)}
          displayTrackingProps={{ category: 'dv-service-layer-product-hints-item' }}
        />
      )}
    </>
  );
};
